import { saasApi } from '../../api'
import type {
  AuditActor,
  AuditLogParams,
  AuditLogResponse,
  CreateMemberPayload,
  CreditLimitPayload,
  CreditsOverview,
  CreditTrend,
  MemberAllowance,
  MemberAllowanceList,
  MemberAllowanceParams,
  MemberListParams,
  MemberListResponse,
  PermissionsOverview,
  RoleCapabilityUpdate,
  TeamMember,
  TeamRoleKey,
  TeamOverview,
  TeamSettings,
  TeamSettingsPatch,
  UsageFilters,
  UsageRange,
  UsageSummary,
} from './teamTypes'

const base = (workspaceId: string) => `/workspaces/${workspaceId}/team`

export const getTeamOverview = (workspaceId: string) => saasApi.get<TeamOverview>(`${base(workspaceId)}/overview`)

export const getTeamCreditTrend = (workspaceId: string, days: number) =>
  saasApi.get<CreditTrend>(`${base(workspaceId)}/overview/credit-trend`, { params: { days } })

export const listTeamMembers = (workspaceId: string, params: MemberListParams) =>
  saasApi.get<MemberListResponse>(`${base(workspaceId)}/members`, { params })

export const getTeamMember = (workspaceId: string, userId: string) =>
  saasApi.get<TeamMember>(`${base(workspaceId)}/members/${userId}`)

export const createTeamMember = (workspaceId: string, payload: CreateMemberPayload) =>
  saasApi.post<TeamMember>(`${base(workspaceId)}/members`, payload)

export const patchTeamMember = (
  workspaceId: string,
  userId: string,
  payload: { display_name?: string; role?: 'admin' | 'editor'; must_change_password?: boolean },
) => saasApi.patch<TeamMember>(`${base(workspaceId)}/members/${userId}`, payload)

export const resetTeamMemberPassword = (
  workspaceId: string,
  userId: string,
  payload: { new_password: string; must_change_password: boolean },
) => saasApi.post<TeamMember>(`${base(workspaceId)}/members/${userId}/reset-password`, payload)

export const disableTeamMember = (workspaceId: string, userId: string) =>
  saasApi.post<TeamMember>(`${base(workspaceId)}/members/${userId}/disable`)

export const restoreTeamMember = (workspaceId: string, userId: string) =>
  saasApi.post<TeamMember>(`${base(workspaceId)}/members/${userId}/restore`)

export const getTeamSettings = (workspaceId: string) => saasApi.get<TeamSettings>(`${base(workspaceId)}/settings`)

export const patchTeamSettings = (workspaceId: string, payload: TeamSettingsPatch) =>
  saasApi.patch<TeamSettings>(`${base(workspaceId)}/settings`, payload)

/* VistoeraTASK-00529 - 积分管理 / 使用统计 */
export const getCreditsOverview = (workspaceId: string, range: UsageRange) =>
  saasApi.get<CreditsOverview>(`${base(workspaceId)}/credits/overview`, { params: { range } })

export const listCreditMembers = (workspaceId: string, params: MemberAllowanceParams) =>
  saasApi.get<MemberAllowanceList>(`${base(workspaceId)}/credits/members`, { params })

export const setMemberCreditLimit = (workspaceId: string, userId: string, payload: CreditLimitPayload) =>
  saasApi.put<MemberAllowance>(`${base(workspaceId)}/members/${userId}/credit-limit`, payload)

export const getUsageSummary = (workspaceId: string, f: UsageFilters) =>
  saasApi.get<UsageSummary>(`${base(workspaceId)}/usage`, {
    params: { range: f.range, user_id: f.user_id, category: f.category, provider: f.provider },
  })

export const changeOwnPassword = (currentPassword: string, newPassword: string) =>
  saasApi.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword })

/** Best human message from a failed call. `saasApi`'s interceptor already
 * unwraps the backend `detail.message` into `Error.message` (+ `.code`). */
export function teamErrorMessage(err: unknown, fallback: string): string {
  const e = err as { message?: string }
  return (e?.message && e.message.trim()) || fallback
}

export function teamErrorCode(err: unknown): string | undefined {
  return (err as { code?: string })?.code
}

/* VistoeraTASK-00532 - 权限管理 / 操作日志 (the audit log is READ ONLY: no write endpoint exists) */
export const getPermissionsOverview = (workspaceId: string) =>
  saasApi.get<PermissionsOverview>(`${base(workspaceId)}/permissions/overview`)

export const putRoleCapabilities = (workspaceId: string, updates: RoleCapabilityUpdate[]) =>
  saasApi.put<PermissionsOverview>(`${base(workspaceId)}/permissions/role-capabilities`, { updates })

export const setMemberRole = (
  workspaceId: string,
  userId: string,
  payload: { role: 'admin' | 'member'; expected_role?: TeamRoleKey },
) => saasApi.put<TeamMember>(`${base(workspaceId)}/members/${userId}/role`, payload)

export const listAuditLogs = (workspaceId: string, params: AuditLogParams) =>
  saasApi.get<AuditLogResponse>(`${base(workspaceId)}/audit-logs`, { params })

export const listAuditActors = (workspaceId: string) =>
  saasApi.get<{ items: AuditActor[] }>(`${base(workspaceId)}/audit-logs/actors`)
